import { persist, createJSONStorage } from "zustand/middleware";

import * as capacitorStorage from "../../../Common/utils/localStorage";
import { LOCAL_STORAGE } from "../../../Common/config";
import { User, UserPowerUps } from "../../../Common/models/user";
import {
  WLStripeEvent,
  WLStripeWPlanSubscription,
} from "../../../Common/models/subscriptions";
import Stripe from "stripe";
import { createWithEqualityFn } from "zustand/traditional";

interface WebUsersState {
  user: User | null | undefined;
  setUser: (state: User) => void;

  stripeActiveSubscription: null | WLStripeWPlanSubscription | undefined;
  setStripeActiveSubscription: (
    state: null | WLStripeWPlanSubscription
  ) => void;

  latestFailedStripePaymentAttempt: null | Stripe.Event | undefined;
  setLatestFailedStripePaymentAttempt: (state: null | Stripe.Event) => void;

  userPhoneNumber: null | string;
  setUserPhoneNumber: (userPhoneNumber: null | string) => void;

  /** Set when redirecting to OTP before full `user` is loaded from Firestore */
  signInOtpFirstName: string | null;
  setSignInOtpFirstName: (firstName: string | null) => void;

  hasBillingIssue: boolean;
  setHasBillingIssue: (hasBillingIssue: boolean) => void;

  userPowerUps: UserPowerUps[];
  setUserPowerUps: (state: UserPowerUps[]) => void;

  totalEntries: number;
  setTotalEntries: (state: number) => void;
  accumulatedEntries: number;
  setAccumulatedEntries: (state: number) => void;
  powerUpEntries: number;
  setPowerUpEntries: (state: number) => void;
  userPowerUpsHistory: UserPowerUps[];
  setUserPowerUpsHistory: (state: UserPowerUps[]) => void;

  resetWebUsersStore: () => Promise<void>;
}

export const useWebUserStore = createWithEqualityFn<
  WebUsersState,
  [["zustand/persist", WebUsersState]]
>(
  persist(
    (set) => ({
      user: undefined as User | null | undefined,
      stripeActiveSubscription: undefined,
      setUser: (newState: User) => {
        set(() => ({ user: newState, signInOtpFirstName: null }));
        const { firstName, lastName, emailAddress } = newState;
        capacitorStorage.setItem(
          LOCAL_STORAGE.userName,
          `${firstName} ${lastName}`
        );
        capacitorStorage.setItem(LOCAL_STORAGE.userEmail, emailAddress);
      },

      setStripeActiveSubscription: (stripeActiveSubscription) =>
        set({ stripeActiveSubscription }),

      latestFailedStripePaymentAttempt: undefined,
      setLatestFailedStripePaymentAttempt: (latestFailedStripePaymentAttempt) =>
        set({ latestFailedStripePaymentAttempt }),

      userPowerUps: [],
      setUserPowerUps: (userPowerUps) => set({ userPowerUps }),
      userPowerUpsHistory: [],
      setUserPowerUpsHistory: (userPowerUpsHistory) =>
        set({ userPowerUpsHistory }),

      totalEntries: 0,
      setTotalEntries: (totalEntries) => set({ totalEntries }),

      accumulatedEntries: 0,
      setAccumulatedEntries: (accumulatedEntries) =>
        set({ accumulatedEntries }),
      powerUpEntries: 0,
      setPowerUpEntries: (powerUpEntries) => set({ powerUpEntries }),

      userPhoneNumber: null,
      setUserPhoneNumber: (userPhoneNumber: null | string) =>
        set({ userPhoneNumber }),

      signInOtpFirstName: null,
      setSignInOtpFirstName: (signInOtpFirstName) =>
        set({ signInOtpFirstName }),

      hasBillingIssue: false,
      setHasBillingIssue: (hasBillingIssue: boolean) =>
        set({ hasBillingIssue }),

      resetWebUsersStore: async () => {
        return set(() => ({
          user: null,
          userPhoneNumber: null,
          signInOtpFirstName: null,
          accumulatedEntries: 0,
          hasBillingIssue: false,
          stripeActiveSubscription: undefined,
          totalEntries: 0,
          powerUpEntries: 0,
          userPowerUps: [],
          userPowerUpsHistory: [],
        }));
      },
    }),
    {
      name: "web-user-detail", // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
    }
  )
);
